import { useState, useEffect, useCallback } from 'react';
import CommunicationsService from 'api/communications/CommunicationsService';
import UsersService from 'api/users/UsersService';
import { CommunicationDto } from 'api/communications/models/CommunicationDto';
import { UsersSelectItemDto } from 'api/users/models/UserDto';
import Logger from 'common/services/Logger';
import { LOGGER_LOG_TYPE } from 'Config';

export interface RecipientInfo {
    id: string;
    name: string;
    email: string;
    phoneNumber?: string;
    hasAlert: boolean;
    hasEmail: boolean;
    hasSms: boolean;
}

interface UseAlarmRecipientsResult {
    recipients: RecipientInfo[];
    loading: boolean;
}

// Maps alarm types to their communication name prefixes
export const alarmTypeToCommunicationName: Record<string, string> = {
    'SPEED_LIMIT': 'ALARM_TRIGGER_SPEED',
    'EXIT_AREA': 'ALARM_TRIGGER_EXIT_AREA',
    'ENTRY_AREA': 'ALARM_TRIGGER_ENTRY_AREA',
    'UNPLUGED': 'ALARM_TRIGGER_UNPLUGED',
    'TOWING': 'ALARM_TRIGGER_TOWING',
    'STAY_IN_AREA': 'ALARM_TRIGGER_STAY_IN_AREA',
    'STAY_OUT_AREA': 'ALARM_TRIGGER_STAY_OUT_AREA',
};

// Parse expression using the same logic as CommunicationsDetails
const parseExpressionForRecipients = (
    expression: string | undefined,
    users: UsersSelectItemDto[]
): Set<string> => {
    const recipientUsernames = new Set<string>();

    if (!expression) return recipientUsernames;

    // Check for AND/OR pattern: (users)AND/OR(roles)
    const andOrRegex = /\((.*)\)(AND|OR)\((.*)\)/gi;
    const andOrMatch = andOrRegex.exec(expression);

    const concatType = andOrMatch ? andOrMatch[2].toUpperCase() : null;
    const usersExpression = andOrMatch ? andOrMatch[1] : expression?.replace(/\(/g, '').replace(/\)/g, '');
    const rolesExpression = andOrMatch ? andOrMatch[3] : '';

    // Parse users from expression
    const usersRegex = /I:([^\s)]+)/gi;
    const userMatches: string[] = [];
    let userMatch = usersRegex.exec(usersExpression || '');
    while (userMatch != null) {
        userMatches.push(userMatch[1]);
        userMatch = usersRegex.exec(usersExpression || '');
    }

    // Get users matching the expression
    const matchedUsernames = new Set<string>();
    if (userMatches.length > 0) {
        if (userMatches.length === 1 && userMatches[0] === '*') {
            // All users
            users.forEach(u => matchedUsernames.add(u.userName));
        } else {
            userMatches.forEach(userName => {
                const user = users.find(u => u.userName === userName);
                if (user) {
                    matchedUsernames.add(user.userName);
                }
            });
        }
    }

    // Parse roles from expression
    const rolesRegex = /R:([^\s)]+)/gi;
    const roleMatches: string[] = [];
    let roleMatch = rolesRegex.exec(rolesExpression || '');
    while (roleMatch != null) {
        roleMatches.push(roleMatch[1]);
        roleMatch = rolesRegex.exec(rolesExpression || '');
    }

    // Get users matching the roles
    const matchedRoleUsernames = new Set<string>();
    if (roleMatches.length > 0) {
        const matchedRoleIds = new Set<string>();
        if (roleMatches.length === 1 && roleMatches[0] === '*') {
            // All roles - include all users
            users.forEach(u => matchedRoleUsernames.add(u.userName));
        } else {
            // Specific roles - find users with those roles
            roleMatches.forEach(roleId => {
                matchedRoleIds.add(roleId);
            });

            // Find users that have any of the matched roles
            if (matchedRoleIds.size > 0) {
                users.forEach(user => {
                    if (user.roleIds?.some(roleId => matchedRoleIds.has(roleId))) {
                        matchedRoleUsernames.add(user.userName);
                    }
                });
            }
        }
    }

    // Combine users and roles based on AND/OR logic
    if (concatType === 'AND') {
        // AND: user must be in BOTH lists (intersection)
        if (matchedUsernames.size > 0 && matchedRoleUsernames.size > 0) {
            matchedUsernames.forEach(u => {
                if (matchedRoleUsernames.has(u)) {
                    recipientUsernames.add(u);
                }
            });
        } else if (matchedUsernames.size > 0 && roleMatches.length === 0) {
            // Only users specified, no roles
            matchedUsernames.forEach(u => recipientUsernames.add(u));
        } else if (matchedRoleUsernames.size > 0 && userMatches.length === 0) {
            // Only roles specified, no users
            matchedRoleUsernames.forEach(u => recipientUsernames.add(u));
        }
    } else {
        // OR or no concat type: include users from either list (union)
        matchedUsernames.forEach(u => recipientUsernames.add(u));
        matchedRoleUsernames.forEach(u => recipientUsernames.add(u));
    }

    return recipientUsernames;
};

export const useAlarmRecipients = (alarmType?: string): UseAlarmRecipientsResult => {
    const [recipients, setRecipients] = useState<RecipientInfo[]>([]);
    const [loading, setLoading] = useState(true);

    const fetchData = useCallback(async () => {
        try {
            setLoading(true);

            // If no alarm type is selected, return empty recipients
            if (!alarmType) {
                setRecipients([]);
                setLoading(false);
                return;
            }

            const [communications, users] = await Promise.all([
                CommunicationsService.getAllForCompany(),
                UsersService.getAllForCompany(),
            ]);

            // Get the communication name prefix for this alarm type
            const communicationNamePrefix = alarmTypeToCommunicationName[alarmType];

            if (!communicationNamePrefix) {
                setRecipients([]);
                setLoading(false);
                return;
            }

            // Find communications for each notification type
            const alertComm = communications.find(
                (c: CommunicationDto) => c.name === `${communicationNamePrefix}_ALERT`
            );
            const emailComm = communications.find(
                (c: CommunicationDto) => c.name === `${communicationNamePrefix}_EMAIL`
            );
            const smsComm = communications.find(
                (c: CommunicationDto) => c.name === `${communicationNamePrefix}_SMS`
            );

            // Get recipients for each type
            const alertRecipients = parseExpressionForRecipients(alertComm?.expression, users);
            const emailRecipients = parseExpressionForRecipients(emailComm?.expression, users);
            const smsRecipients = parseExpressionForRecipients(smsComm?.expression, users);

            // Collect all unique usernames from all types
            const allUsernames = new Set<string>();
            alertRecipients.forEach(u => allUsernames.add(u));
            emailRecipients.forEach(u => allUsernames.add(u));
            smsRecipients.forEach(u => allUsernames.add(u));

            // Map usernames to user info with notification flags
            const recipientList: RecipientInfo[] = [];
            allUsernames.forEach(username => {
                const user = users.find((u: UsersSelectItemDto) => u.userName === username);
                if (user) {
                    recipientList.push({
                        id: user.id,
                        name: user.realName,
                        email: user.email,
                        phoneNumber: user.phoneNumber,
                        hasAlert: alertRecipients.has(username),
                        hasEmail: emailRecipients.has(username),
                        hasSms: smsRecipients.has(username),
                    });
                }
            });

            setRecipients(recipientList);
        } catch (error) {
            Logger.error(LOGGER_LOG_TYPE.REQUEST, 'Error fetching alarm recipients', error);
            setRecipients([]);
        } finally {
            setLoading(false);
        }
    }, [alarmType]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    return { recipients, loading };
};

export default useAlarmRecipients;
